// Map page — the primary interface
// Full-screen map with GPS, waypoints, catches, and floating action buttons

import { useState, useEffect, useCallback } from 'react';
import { MapPin, Fish, Navigation, Plus, X } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { MapView } from '@/components/MapView';
import { LogCatchModal } from '@/components/LogCatchModal';
import { SpotSheet } from '@/components/SpotSheet';
import { useGps } from '@/lib/gps';
import {
  getAllWaypoints, saveWaypoint, getAllCatches, getAllTrips, saveTrip
} from '@/lib/local-db';
import type { Waypoint, Catch, Trip } from '@/lib/world-model';
import { SPOTS } from '@/data/spot';
import type { SpotData } from '@/data/spot';

type MapMode = 'view' | 'drop-waypoint' | 'log-catch';

export function MapPage() {
  const { position } = useGps();
  const [waypoints, setWaypoints] = useState<Waypoint[]>([]);
  const [catches, setCatches] = useState<Catch[]>([]);
  const [mode, setMode] = useState<MapMode>('view');
  const [pendingLat, setPendingLat] = useState<number | null>(null);
  const [pendingLng, setPendingLng] = useState<number | null>(null);
  const [showFab, setShowFab] = useState(false);
  const [activeTrip, setActiveTrip] = useState<Trip | null>(null);
  const [selectedSpot, setSelectedSpot] = useState<SpotData | null>(null);

  // Load persisted data
  useEffect(() => {
    getAllWaypoints().then(setWaypoints);
    getAllCatches().then(setCatches);
  }, []);

  // Find or create an active trip
  useEffect(() => {
    getAllTrips().then(trips => {
      const open = trips.find(t => !t.endedAt);
      if (open) {
        setActiveTrip(open);
      }
    });
  }, []);

  const ensureActiveTrip = useCallback(async (): Promise<Trip> => {
    if (activeTrip) return activeTrip;
    const now = new Date().toISOString();
    const trip: Trip = {
      id: uuidv4(),
      name: `Trip — ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`,
      startedAt: now,
      catchIds: [],
      waypointIds: [],
      syncStatus: 'local',
      createdAt: now,
      updatedAt: now,
    };
    await saveTrip(trip);
    setActiveTrip(trip);
    return trip;
  }, [activeTrip]);

  const handleMapClick = useCallback(async (lat: number, lng: number) => {
    if (mode === 'drop-waypoint') {
      setPendingLat(lat);
      setPendingLng(lng);
    } else if (mode === 'log-catch') {
      setPendingLat(lat);
      setPendingLng(lng);
    }
    setShowFab(false);
  }, [mode]);

  const handleDropWaypoint = async () => {
    const lat = pendingLat ?? position?.lat;
    const lng = pendingLng ?? position?.lng;
    if (lat == null || lng == null) return;

    const trip = await ensureActiveTrip();
    const now = new Date().toISOString();
    const label = prompt('Waypoint name?', `Waypoint ${waypoints.length + 1}`);
    if (!label) return;

    const wp: Waypoint = {
      id: uuidv4(),
      tripId: trip.id,
      label,
      lat,
      lng,
      type: 'general',
      createdAt: now,
      updatedAt: now,
    };
    await saveWaypoint(wp);
    setWaypoints(prev => [...prev, wp]);
    setPendingLat(null);
    setPendingLng(null);
    setMode('view');
  };

  const handleLogCatchStart = async () => {
    const lat = pendingLat ?? position?.lat;
    const lng = pendingLng ?? position?.lng;
    if (lat == null || lng == null) return;
    await ensureActiveTrip();
    setMode('log-catch');
    setShowFab(false);
  };

  const handleCatchSaved = (c: Catch) => {
    setCatches(prev => [...prev, c]);
    setPendingLat(null);
    setPendingLng(null);
    setMode('view');
  };

  const cancelPending = () => {
    setPendingLat(null);
    setPendingLng(null);
    setMode('view');
    setShowFab(false);
  };

  const catchLat = pendingLat ?? position?.lat ?? 43.8231;
  const catchLng = pendingLng ?? position?.lng ?? -70.4617;

  return (
    <div className="relative w-full h-full bg-black">
      {/* Map fills the screen */}
      <MapView
        waypoints={waypoints}
        catches={catches}
        spots={SPOTS}
        onMapClick={handleMapClick}
        onSpotClick={setSelectedSpot}
      />

      {/* Mode instruction banner */}
      {mode === 'drop-waypoint' && (
        <div className="absolute top-16 left-1/2 -translate-x-1/2 z-20 bg-black/80 backdrop-blur text-white px-4 py-2 rounded-full text-sm flex items-center gap-2">
          <MapPin className="w-4 h-4 text-[#007AFF]" />
          Tap map to drop waypoint
          <button onClick={cancelPending}><X className="w-4 h-4 ml-1 text-[#8e8e93]" /></button>
        </div>
      )}
      {mode === 'log-catch' && pendingLat == null && (
        <div className="absolute top-16 left-1/2 -translate-x-1/2 z-20 bg-black/80 backdrop-blur text-white px-4 py-2 rounded-full text-sm flex items-center gap-2">
          <Fish className="w-4 h-4 text-[#FFD700]" />
          Tap map or use GPS location
          <button onClick={cancelPending}><X className="w-4 h-4 ml-1 text-[#8e8e93]" /></button>
        </div>
      )}

      {/* Pending waypoint action */}
      {mode === 'drop-waypoint' && pendingLat != null && (
        <div className="absolute bottom-28 left-0 right-0 flex justify-center z-20 px-4">
          <div className="bg-[#1c1c1e] rounded-2xl p-4 w-full max-w-sm flex gap-3">
            <button
              onClick={handleDropWaypoint}
              className="flex-1 py-3 rounded-xl bg-[#1a4a2e] text-white font-semibold text-sm"
            >
              Drop here
            </button>
            <button
              onClick={cancelPending}
              className="px-4 py-3 rounded-xl bg-[#2c2c2e] text-[#8e8e93] text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* FAB */}
      {mode === 'view' && (
        <div className="absolute bottom-28 right-4 z-20 flex flex-col items-end gap-3">
          {showFab && (
            <>
              <FabButton
                label="Log Catch"
                icon={<Fish className="w-5 h-5" />}
                color="bg-[#FFD700]"
                textColor="text-[#1c1c1e]"
                onClick={async () => {
                  setPendingLat(position?.lat ?? null);
                  setPendingLng(position?.lng ?? null);
                  setMode('log-catch');
                  setShowFab(false);
                }}
              />
              <FabButton
                label="Drop Waypoint"
                icon={<MapPin className="w-5 h-5" />}
                color="bg-[#007AFF]"
                textColor="text-white"
                onClick={() => {
                  setMode('drop-waypoint');
                  setShowFab(false);
                }}
              />
              <FabButton
                label="Center GPS"
                icon={<Navigation className="w-5 h-5" />}
                color="bg-[#2c2c2e]"
                textColor="text-white"
                onClick={() => {
                  // The map auto-centers on first GPS fix;
                  // subsequent manual re-center would require map ref access
                  setShowFab(false);
                }}
              />
            </>
          )}
          <button
            onClick={() => setShowFab(f => !f)}
            className={`w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all ${
              showFab ? 'bg-[#d45a1a] rotate-45' : 'bg-[#1a4a2e]'
            }`}
          >
            <Plus className="w-7 h-7 text-white" />
          </button>
        </div>
      )}

      {/* Log catch modal */}
      {mode === 'log-catch' && pendingLat != null && (
        <LogCatchModal
          tripId={activeTrip?.id ?? 'unsaved'}
          lat={catchLat}
          lng={catchLng}
          onSave={handleCatchSaved}
          onClose={cancelPending}
        />
      )}
      {/* Spot detail sheet */}
      {selectedSpot && (
        <SpotSheet spot={selectedSpot} onClose={() => setSelectedSpot(null)} />
      )}
    </div>
  );
}

function FabButton({
  label, icon, color, textColor, onClick,
}: {
  label: string;
  icon: React.ReactNode;
  color: string;
  textColor: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 pl-3 pr-4 h-11 rounded-full ${color} ${textColor} font-semibold text-sm shadow-lg active:scale-95 transition`}
    >
      {icon}
      {label}
    </button>
  );
}
